import { produce } from 'immer';
import { StateCreator } from 'zustand';
import { devtools } from 'zustand/middleware';
import { toZonedTime } from 'date-fns-tz';
import { DEFAULT_SKU } from '@/constants';
import { format, parseISO } from 'date-fns';
import { Location, Sku, Slot, SlotsItem } from '../types';
import { compareDateWithFirstAvailableBookingDate } from '@/utils/sku370';

export interface ScanSlice {
  scan: {
    bookingDate: number | null;
    closestAvailableDate: string | null;
    date: Date | undefined;
    eventId: string | undefined;
    location: Location | null;
    locationId: string;
    locations: Location[];
    machineId: string;
    session: any;
    sku: Sku | null;
    skuId: number;
    slot: Slot | null;
    slots: SlotsItem[];
    timezone: string | undefined;
    errors: {
      [key: string]: string;
    };
    methods: {
      setLocations: (locations: Location[]) => void;
      setLocation: (scanLocation: Location) => void;
      setSku: (sku: Sku) => void;
      setSlots: (slots: SlotsItem[]) => void;
      setSlot: (slot: Slot | null) => void;
      setDate: (date: Date | undefined) => void;
      setMachineId: (machineId: string) => void;
      setEventId: (eventId: string | undefined) => void;
      setClosestAvailableDate: (date: string | null) => void;
      getErrors: () => { [key: string]: string };
      getAll: () => any;
      setError: (field: string, message: string) => void;
      clearErrors: () => void;
      removeError: (field: string) => void;
      setSession: (session: any) => void;
      clearScan: () => void;
      clearSlots: () => void;
      isReferralRequired: () => boolean;
    };
  };
}

const createScanSlice: StateCreator<ScanSlice, [['zustand/devtools', never]]> = (set, get) => ({
  scan: {
    bookingDate: null,
    errors: {},
    date: undefined,
    locations: [],
    location: null,
    locationId: '',
    skuId: DEFAULT_SKU,
    machineId: '',
    slots: [],
    slot: null,
    session: undefined,
    closestAvailableDate: null,
    timezone: undefined,
    eventId: undefined,
    sku: null,
    methods: {
      setLocations: (locations: Location[]) =>
        set(
          produce((state) => {
            state.scan.locations = locations;
          }),
          false,
          'scan/setLocations', // Action name
        ),
      setLocation: (scanLocation: Location) =>
        set(
          produce((state) => {
            state.scan.locationId = scanLocation.code;
            state.scan.location = scanLocation;
            state.scan.timezone = scanLocation.timezone;
          }),
          false,
          'scan/setLocation', // Action name
        ),
      setSku: (sku: Sku) =>
        set(
          produce((state) => {
            state.scan.sku = sku;
            state.scan.skuId = +sku?.skuID;
          }),
          false,
          'scan/setSku', // Action name
        ),
      setSlots: (slots: SlotsItem[]) =>
        set(
          produce((state) => {
            state.scan.slots = slots;
          }),
          false,
          'scan/setSlots', // Action name
        ),
      setSlot: (slot: Slot | null) =>
        set(
          produce((state) => {
            state.scan.slot = slot;
            state.scan.date = slot
              ? toZonedTime(new Date(slot.utcStart * 1000), state.scan.timezone)
              : null;

            const errors = get().scan.errors;
            if (
              Object.keys(errors).length === 2 &&
              errors?.['slot'] &&
              errors?.['cannotProceedWithoutSelectingAScanDate'] &&
              slot
            ) {
              state.scan.errors = {};
            }
          }),
          false,
          'scan/setSlot', // Action name
        ),
      setDate: (date: Date | undefined) =>
        set(
          produce((state) => {
            state.scan.date = compareDateWithFirstAvailableBookingDate(
              date,
              state.scan.skuId,
              state.scan.locationId,
            );
          }),
          false,
          'scan/setDate', // Action name
        ),
      setClosestAvailableDate: (date: string | null) =>
        set(
          produce((state) => {
            state.scan.closestAvailableDate = date;
          }),
          false,
          'scan/setClosestAvailableDate', // Action name
        ),
      clearScan: () =>
        set(
          produce((state) => {
            // We dont need to clear the date, as it could be useful to keep the calendar date selected
            state.scan.slot = null;
            state.scan.session = null;
            state.scan.machineId = '';
            state.scan.eventId = '';
          }),
          false,
          'scan/clearScan', // Action name
        ),
      clearSlots: () =>
        set(
          produce((state) => {
            state.scan.session = null;
            state.scan.slots = [];
            state.scan.slot = null;
            state.scan.closestAvailableDate = null;
            state.scan.date = undefined;
            state.scan.machineId = '';
            state.scan.eventId = undefined;
            state.scan.timezone = undefined;
            state.scan.skuId = DEFAULT_SKU;
          }),
          false,
          'scan/clearSlots', // Action name
        ),
      setMachineId: (machineId: string) =>
        set(
          produce((state) => {
            state.scan.machineId = machineId;
          }),
          false,
          'scan/setMachineId', // Action name
        ),
      setEventId: (eventId: string | undefined) =>
        set(
          produce((state) => {
            state.scan.eventId = eventId;
          }),
          false,
          'scan/setEventId', // Action name
        ),
      getAll: () => {
        const { errors, methods, ...memberProperties } = get().scan;
        return memberProperties;
      },
      getErrors: () => {
        return get().scan.errors;
      },
      setError: (field, message) =>
        set(
          produce((state) => {
            state.scan.errors[field] = message;
          }),
          false,
          'scan/setError', // Action name
        ),
      clearErrors: () =>
        set(
          produce((state) => {
            state.scan.errors = {};
          }),
          false,
          'scan/clearErrors', // Action name
        ),
      removeError: (field) =>
        set(
          produce((state) => {
            delete state.scan.errors[field];
          }),
          false,
          'scan/removeError', // Action name
        ),
      setSession: (session) =>
        set(
          produce((state) => {
            if (!session) {
              state.scan.session = session;
              return;
            }
            state.scan.locationId = session?.locationId || '';
            state.scan.skuId = session?.skuId || DEFAULT_SKU;
            state.scan.machineId = session?.timeSlot?.machineId || '';
            state.scan.slot = session?.timeSlot
              ? {
                  machines: [session.timeSlot.machineId],
                  utcStart: session.timeSlot.utcStart,
                  utcEnd: session.timeSlot.utcEnd,
                }
              : null;
            state.scan.date = session?.timeSlot?.utcStart
              ? toZonedTime(new Date(session?.timeSlot?.utcStart * 1000), state.scan.timezone)
              : state.scan.date;
            state.scan.eventId = session?.eventId || undefined;
            state.scan.session = session;
            // if user is logged in, we don't need to set the user info from the session
            if (!session?.member || state.user.isLoggedIn) return;
            state.user.lastName = session?.member?.lastname || '';
            state.user.firstName = session?.member?.firstname || '';
            state.user.email = session?.member?.email || '';
            state.user.dob = session?.member?.dob
              ? format(parseISO(session?.member?.dob), 'MM/dd/yyyy')
              : '';
            state.user.gender = session?.member?.gender || '';
            state.user.preferredName = session?.member?.preferredName || '';
            state.user.phoneNumber = session?.member?.phoneNumber || '';
          }),
          false,
          'scan/setSession', // Action name
        ),
      isReferralRequired: () => {
        // return get().scan.location?.referralRequired === true;}
        return get().scan.location?.code !== 'usa-ma-001'; // EC-127: hardcoding to filter referral for MA location
      },
    },
  },
});

export default devtools(createScanSlice, { name: 'ScanSlice' });
